<template>
  <Card clickable class="h-[280px]" :custom-classes="customClasses" :href="profileUrl" :target="target">
    <div class="-mx-16 -mt-16 h-[162px] overflow-hidden rounded-t-xl bg-grey-divider-subdued">
      <img v-if="photoUrl" :src="photoUrl" :alt="`Avatar of ${name}`" class="h-full w-full object-cover" />
      <div v-else aria-hidden="true" class="flex h-full w-full items-center justify-center font-medium text-grey-clickable">
        <span class="text-l">{{ initials }}</span>
      </div>
    </div>
    <div class="h-[107px]">
      <h3 class="text-strong-body mb-4 text-ellipsis overflow-hidden whitespace-nowrap">
        {{ nameTruncated }}
      </h3>
      <p v-if="experienceDisplay" class="text-small mb-4 text-ellipsis overflow-hidden whitespace-nowrap">
        {{ experienceDisplay }}
      </p>
      <p v-if="hasRate" class="text-small">${{ minRateDisplay }}–{{ maxRateDisplay }}/hr</p>
      <StarRating v-if="ratingsCount" class="-mt-4" :count="ratingsCount" :average="avgRating ?? 0" :star-size="16" />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import Card from '@/components/card/Card.vue';
import StarRating from '@/components/star_rating/StarRating.vue';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import type { LinkTarget } from '@/helpers/redirects';

const props = withDefaults(
  defineProps<{
    name: string;
    sitterId: string;
    photoUrl?: string;
    yearsExperience?: number | null;
    minRate?: number | null;
    maxRate?: number | null;
    avgRating?: number | null;
    totalRatings?: number | null;
    careType?: string;
    target?: LinkTarget;
    customClasses?: string;
  }>(),
  {
    photoUrl: '',
    yearsExperience: null,
    minRate: null,
    maxRate: null,
    avgRating: null,
    totalRatings: null,
    careType: 'babysitter',
    target: '_self',
    customClasses: '',
  }
);

// Mirrors isChildCare from apps/provider/profile/__types__/CareTypes — components/ must not import from apps/
const isChildCare = computed(() => props.careType === 'babysitter' || props.careType === 'nanny');

const nameTruncated = computed(() => (props.name.length > 20 ? `${props.name.substring(0, 20)}...` : props.name));

const initials = computed(() => {
  if (!props.name) return '';
  const match = props.name.match(/^([A-Za-z])[^\s]+\s+([A-Za-z])/);
  return match ? `${match[1]}${match[2]}`.toUpperCase() : props.name.charAt(0).toUpperCase();
});

const experienceDisplay = computed(() => {
  const years = props.yearsExperience ?? 0;
  if (years < 1) return '';
  const unit = years === 1 ? 'year' : 'years';
  return isChildCare.value ? `${years} ${unit} professional experience` : `${years} ${unit} experience`;
});

// Rate rounding mirrors the legacy Rails PayRateInfo presenter
const minRateDisplay = computed(() => Math.floor((props.minRate ?? 0) / 100));
const maxRateDisplay = computed(() => Math.ceil((props.maxRate ?? 0) / 100));
const hasRate = computed(() => minRateDisplay.value > 0 && maxRateDisplay.value > 0);

const ratingsCount = computed(() => props.totalRatings ?? 0);

const profileUrl = computed(() => getProviderProfileUrl(props.sitterId, props.careType) ?? false);
</script>
